<template>
  <div class="tab-container">
    <div class="tab-header">
      <div class="d-flex flex-column">
        <span class="ap-title ap-title__main">{{ $t('userConfiguration.password') }}</span>
        <span class="ap-subtitle ap-subtitle__main">{{ $t('userConfiguration.passwordSub') }}</span>
      </div>
    </div>
    <div class="tab-body">
      <b-container>
        <b-row class="py-2 underline-row bottom-bordered">
          <b-col class="d-flex align-items-center pl-0">
            <div class="d-flex flex-column">
              <span class="option">{{ $t('userConfiguration.currentPassword') }}</span>
            </div>
          </b-col>
          <b-col
            cols="5"
            class="d-flex justify-content-end pr-0 flex-column"
          >
            <vt-input
              v-model="currentPassword"
              class="form-control-sm"
              :type="showPassword ? 'text' : 'password'"
              :class="{'empty-field': currentPasswordErrorMsg}"
            />
            <small
              v-if="currentPasswordErrorMsg"
              class="error"
            >{{ currentPasswordErrorMsg }}</small>
          </b-col>
        </b-row>
        <b-row class="py-2 underline-row bottom-bordered">
          <b-col class="d-flex align-items-center pl-0">
            <div class="d-flex flex-column">
              <span class="option">{{ $t('userConfiguration.newPassword') }}</span>
            </div>
          </b-col>
          <b-col
            cols="5"
            class="d-flex justify-content-end pr-0 flex-column"
          >
            <vt-input
              v-model="newPassword"
              class="form-control-sm"
              :type="showPassword ? 'text' : 'password'"
              :class="{'empty-field': newPasswordErrorMsg}"
            />
            <small
              v-if="newPasswordErrorMsg"
              class="error"
            >{{ newPasswordErrorMsg }}</small>
          </b-col>
        </b-row>
        <b-row class="py-2 underline-row bottom-bordered">
          <b-col class="d-flex align-items-center pl-0">
            <div class="d-flex flex-column">
              <span class="option">{{ $t('userConfiguration.repeatPassword') }}</span>
            </div>
          </b-col>
          <b-col
            cols="5"
            class="d-flex justify-content-end pr-0 flex-column"
          >
            <vt-input
              v-model="repeatedNewPassword"
              class="form-control-sm"
              :type="showPassword ? 'text' : 'password'"
              :class="{'empty-field': repeatedNewPasswordErrorMsg}"
            />
            <small
              v-if="repeatedNewPasswordErrorMsg"
              class="error"
            >{{ repeatedNewPasswordErrorMsg }}</small>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col class="d-flex justify-content-end pr-0">
            <b-button
              class="btn__link pr-0"
              @click="showPassword = !showPassword"
            >
              <span v-if="!showPassword">{{ $tc('global.showPasswords') }}<span class="far fa-eye ml-2" /></span>
              <span v-if="showPassword">{{ $tc('global.hidePasswords') }}<span class="far fa-eye-slash ml-2" /></span>
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div class="tab-footer">
      <vt-loading-button
        class="btn--primary btn-sm"
        :params="{ promise: changePassword }"
      >
        <i
          class="far fa-save mr-2"
          aria-hidden="true"
        />
        {{ $t('global.save') }}
      </vt-loading-button>
    </div>
  </div>
</template>

<script>
import { auth } from '@/api/auth'

export default {
    name: 'PasswordModal',
    props: {
        userInformation: { type: Object, required: true }
    },
    data() {
        return {
            currentPassword: null,
            newPassword: null,
            repeatedNewPassword: null,
            currentPasswordErrorMsg: null,
            newPasswordErrorMsg: null,
            repeatedNewPasswordErrorMsg: null,
            showPassword: false
        }
    },
    computed: {
        passwordMinimumLength() {
            return this.$store.getters.parameters.global?.passwordMinimumLength
        },
        passwordMaximumLength() {
            return this.$store.getters.parameters.global?.passwordMaximumLength
        }
    },
    methods: {
        show() {
            this.clearErrors()
            this.$refs.passwordModal.show()
        },
        hide() {
            this.$refs.passwordModal.hide()
        },
        clearErrors() {
            this.currentPasswordErrorMsg = null
            this.newPasswordErrorMsg = null
            this.repeatedNewPasswordErrorMsg = null
        },
        async changePassword() {
            if (this.validPasswords()) {
                const [err] = await auth.changePassword(this.currentPassword, this.newPassword)
                if (err) {
                    const data = err.response.data
                    let errorMessage = null
                    if (data && data.code === this.$apiExceptions.INVALID_PASSWORD) {
                        errorMessage = this.$t('login.error.invalidPassword')
                    } else if (data && data.code === this.$apiExceptions.REPEATED_PASSWORD) {
                        errorMessage = this.$t('login.error.passwordPreviouslyUsed')
                    }
                    this.showErrorModalGlobal(errorMessage)
                } else {
                    this.showSuccessModalGlobal(this.$t('userConfiguration.successfulPasswordChange'))
                    this.hide()
                }
            }
        },
        validPasswords() {
            this.clearErrors()
            let valid = true
            if (!this.currentPassword) {
                this.currentPasswordErrorMsg = this.$t('userConfiguration.emptyCurrentPassword')
                valid = false
            }
            this.newPasswordErrorMsg = this.checkNewPassword(this.newPassword)
            if (this.newPasswordErrorMsg) {
                valid = false
            }
            if (this.newPassword !== this.repeatedNewPassword) {
                this.repeatedNewPasswordErrorMsg = this.$t('userConfiguration.newPasswordMismatch')
                valid = false
            }
            return valid
        },
        checkNewPassword(password) {
            if (!password) {
                return this.$t('userConfiguration.emptyNewPassword')
            }
            if (password.length < this.passwordMinimumLength) {
                return this.$t('userConfiguration.passwordMinimumLengthError', { minimumLength: this.passwordMinimumLength })
            }
            if (password.length > this.passwordMaximumLength) {
                return this.$t('userConfiguration.passwordMaximumLengthError', { maximumLength: this.passwordMaximumLength })
            }
        }
    }
}
</script>

<style lang="scss">
    .password-user-conf .color-main i{
        color: vt-color(primary) !important;
    }
    .password-user-conf .color-main span{
        color: vt-color(primary) !important;
    }
    .underline-row {
        border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    }
    .password-link-button {
        padding: 0;
        font-size: font-size(very-small);
    }
    .password-card span{
        font-size: font-size(small);
    }

    .password-card i{
        font-size: 18px;
    }

    .password-card{
        text-align: justify;
        line-height: 16px;
        box-shadow: 0 2px 5px 0 #9c9c9c;
    }
    .password-user-conf .empty-field {
        border-color: vt-color(red-medium);
    }
</style>
